// HomePage.js - Premium Rebrand
import React from 'react';
import { useBooking } from '../hooks/useBooking';
import { useCurrency } from '../context/CurrencyContext';
import Header from '../components/common/Header';
import SearchForm from '../components/search/SearchForm';
import APIService from '../services/api';
import { PAGES } from '../utils/constants';
import './HomePage.css';

// Import images for Webpack resolution
import heroAirport from '../assets/images/landing/hero-airport.jpg';
import vehicleEconomy from '../assets/images/landing/vehicle-economy.jpg';
import vehicleStandardClass from '../assets/images/landing/vehicle-standard-class.jpg';
import vehicleStandardVan from '../assets/images/landing/vehicle-standard-van.jpg';
import vehicleMinibus from '../assets/images/landing/vehicle-minibus.jpg';

const HomePage = () => {
  const { setCurrentPage, setSearchData, setLoading, state: { selectedService } } = useBooking();
  const { selectedCurrency } = useCurrency();

  // Scoped body class for global overrides (like Google Maps Autocomplete)
  React.useEffect(() => {
    document.body.classList.add('landing-page-active');
    return () => document.body.classList.remove('landing-page-active');
  }, []);

  const handleSearch = async (searchFormData) => {
    try {
      setLoading(true);
      setCurrentPage(PAGES.LOADING);

      const searchParams = {
        fromPlace: searchFormData.fromPlace,
        fromLat: searchFormData.fromLat,
        fromLng: searchFormData.fromLng,
        toPlace: searchFormData.toPlace,
        toLat: searchFormData.toLat,
        toLng: searchFormData.toLng,
        pickupAt: searchFormData.pickupAt.toISOString(),
        pax: searchFormData.passengers,
        hours: searchFormData.hours,
        type: searchFormData.type,
        currency: selectedCurrency?.code || 'USD',
      };

      const searchResults = searchFormData.subService === 'hourly'
        ? await APIService.searchHourly(searchParams)
        : await APIService.searchTransfer(searchParams);

      setSearchData({ searchFormData, searchResults });
      setCurrentPage(PAGES.SEARCH);
    } catch (error) {
      console.error('Search failed:', error);
      alert('Search failed. Please try again.');
      setCurrentPage(PAGES.HOME);
    } finally {
      setLoading(false);
    }
  };

  // WhatsApp click handler
  const handleWhatsAppClick = () => {
    const whatsappNumber = "94774052202";
    const message = encodeURIComponent("Hi! I'm interested in booking a transfer with LankaWayFinder. Can you help me?");
    window.open(`https://wa.me/${whatsappNumber}?text=${message}`, '_blank');
  };

  return (
    <div className="landing-page">
      <main className="page-shell">
        {/* HERO SECTION */}
        <section className="hero">
          <div className="hero-media"></div>
          <div className="hero-overlay"></div>
          <div className="hero-grid"></div>
          <div className="hero-aurora hero-aurora-large"></div>
          <div className="hero-aurora hero-aurora-small"></div>

          <div className="container hero-inner">
            <Header transparent />

            <div className="hero-content">
              <div className="hero-copy">
                <span className="glass-pill eyebrow">
                  <svg className="icon-sm" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M2 22h20" /><path d="M3.77 10.77 2 9l2-4.5 1.1.55c.55.28.9.84.9 1.45s.35 1.17.9 1.45L8 8.5l3-6 1.05.53a2 2 0 0 1 1.09 1.52l.72 5.4a2 2 0 0 0 1.09 1.52l4.4 2.2c.42.22.78.55 1.01.96l.6 1.03c.49.88-.06 1.98-1.06 2.1l-1.9.23" /></svg>
                  Premium airport concierge from CMB
                </span>
                <div className="hero-copy-panel">
                  <div className="eyebrow-row">
                    <span className="mono-label">Sri Lanka airport transfers</span>
                    <span className="eyebrow-line"></span>
                  </div>
                  <h1>
                    <span className="sr-only">Sri Lanka Airport Transfers from CMB — </span>
                    Arrive in Sri Lanka
                    <span>with a chauffeur already waiting.</span>
                  </h1>
                  <p className="hero-kicker">Request a ride, hop in, and go.</p>
                  <p className="hero-description">
                    Sri Lanka arrivals should feel calm, polished, and unmistakably premium. From
                    Colombo hotels to Kandy hills and Galle coastlines, your route is staged before
                    touchdown.
                  </p>

                  <div className="stats-grid">
                    <article className="hero-stat-card">
                      <p className="mono-label gold">Trust</p>
                      <strong>4.98★</strong>
                      <span>12k+ verified airport rides</span>
                    </article>
                    <article className="hero-stat-card">
                      <p className="mono-label gold">Arrival</p>
                      <strong>On-site</strong>
                      <span>Meet-and-greet at CMB</span>
                    </article>
                    <article className="hero-stat-card">
                      <p className="mono-label gold">Coverage</p>
                      <strong>Islandwide</strong>
                      <span>Hotels, cities, and coastlines</span>
                    </article>
                  </div>
                </div>

                <div className="routes-grid">
                  <button className="route-card" onClick={() => { }}>
                    <strong>CMB to Colombo</strong>
                    <span>Hotels in 55 min</span>
                    <em>Most booked</em>
                  </button>
                  <button className="route-card" onClick={() => { }}>
                    <strong>CMB to Negombo</strong>
                    <span>Fast airport exit</span>
                    <em>Quick ride</em>
                  </button>
                  <button className="route-card" onClick={() => { }}>
                    <strong>CMB to Kandy</strong>
                    <span>Long-distance comfort</span>
                    <em>Scenic route</em>
                  </button>
                  <button className="route-card" onClick={() => { }}>
                    <strong>CMB to Galle</strong>
                    <span>Coastal transfer</span>
                    <em>Premium pick</em>
                  </button>
                </div>
              </div>

              {/* BOOKING WIDGET */}
              <aside className="glass-panel booking-card" id="booking-widget">
                <div className="booking-header">
                  <div className="booking-header-text">
                    <svg className="icon-lg booking-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style={{ color: '#3b82f6', marginBottom: '4px' }}><path d="M17.8 19.2 16 11l3.5-3.5C21 6 21.5 4 21 3c-1-.5-3 0-4.5 1.5L13 8 4.8 6.2c-.5-.1-.9.1-1.1.5l-.3.5c-.2.5-.1 1 .3 1.3L9 12l-2 3H4l-1 1 3 2 2 3 1-1v-3l3-2 3.5 5.3c.3.4.8.5 1.3.3l.5-.2c.4-.3.6-.7.5-1.2z" /></svg>
                    <p className="booking-title">Airport Service</p>
                    <p className="booking-subtitle">Book your premium Sri Lanka transfer.</p>
                  </div>
                </div>
                <div style={{ marginTop: '20px' }}>
                  <SearchForm
                    onSearch={handleSearch}
                    serviceType={selectedService}
                    premium
                  />
                </div>
              </aside>
            </div>
          </div>
        </section>

        {/* TRUST BAR */}
        <section className="container trust-bar" aria-label="Trust indicators">
          <div className="glass-panel trust-grid">
            <article className="trust-item">
              <div className="trust-icon gold-icon">
                <svg className="icon-md" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" /></svg>
              </div>
              <strong>4.98 average rating</strong>
              <span>12k+ airport rides</span>
            </article>
            <article className="trust-item">
              <div className="trust-icon gold-icon">
                <svg className="icon-md" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M2 22h20" /><path d="M3.77 10.77 2 9l2-4.5 1.1.55c.55.28.9.84.9 1.45s.35 1.17.9 1.45L8 8.5l3-6 1.05.53a2 2 0 0 1 1.09 1.52l.72 5.4a2 2 0 0 0 1.09 1.52l4.4 2.2c.42.22.78.55 1.01.96l.6 1.03c.49.88-.06 1.98-1.06 2.1l-1.9.23" /></svg>
              </div>
              <strong>Flight tracking ready</strong>
              <span>Driver timing auto-adjusts</span>
            </article>
            <article className="trust-item">
              <div className="trust-icon gold-icon">
                <svg className="icon-md" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z" /><path d="m9 12 2 2 4-4" /></svg>
              </div>
              <strong>Vetted chauffeurs</strong>
              <span>Meet-and-greet included</span>
            </article>
            <article className="trust-item">
              <div className="trust-icon gold-icon">
                <svg className="icon-md" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3.85 8.62a4 4 0 0 1 4.78-4.77 4 4 0 0 1 6.74 0 4 4 0 0 1 4.78 4.78 4 4 0 0 1 0 6.74 4 4 0 0 1-4.77 4.78 4 4 0 0 1-6.75 0 4 4 0 0 1-4.78-4.77 4 4 0 0 1 0-6.76z" /><path d="m9 12 2 2 4-4" /></svg>
              </div>
              <strong>Clear pricing</strong>
              <span>Taxes shown before checkout</span>
            </article>
          </div>
        </section>

        {/* FEATURES SECTION */}
        <section className="light-surface features" aria-label="Features">
          <div className="container">
            <h2 className="section-heading" style={{ fontSize: '2.5rem', fontWeight: '600', color: '#0f172a', marginBottom: '48px', textAlign: 'center' }}>Why choose LankaWayFinder</h2>
          </div>
          <div className="container features-grid">
            <article className="feature-card">
              <div className="feature-icon-wrap">
                <svg className="icon-lg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10" /><path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20" /><path d="M2 12h20" /></svg>
              </div>
              <h3>Global-standard arrivals</h3>
              <p>Airport-grade coordination with Sri Lanka route knowledge baked into every pickup.</p>
            </article>
            <article className="feature-card">
              <div className="feature-icon-wrap">
                <svg className="icon-lg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z" /><path d="m9 12 2 2 4-4" /></svg>
              </div>
              <h3>Professional drivers</h3>
              <p>Verified chauffeurs, clear name-board arrivals, and comfort-first handoff at CMB.</p>
            </article>
            <article className="feature-card">
              <div className="feature-icon-wrap">
                <svg className="icon-lg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16.5 12" /></svg>
              </div>
              <h3>Chauffeur by the hour</h3>
              <p>Flexible hourly service for meetings, hotel changes, and coastal day plans.</p>
            </article>
            <article className="feature-card">
              <div className="feature-icon-wrap">
                <svg className="icon-lg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z" /><path d="M20 3v4" /><path d="M22 5h-4" /><path d="M4 17v2" /><path d="M5 18H3" /></svg>
              </div>
              <h3>City rides with polish</h3>
              <p>A premium alternative to taxis for Colombo hotels, Negombo stays, and long-distance transfers.</p>
            </article>
          </div>
        </section>

        {/* HOW IT WORKS */}
        <section className="light-surface how-it-works" aria-label="How it works">
          <div className="container steps-shell">
            <h2>How It Works</h2>
            <div className="steps-grid">
              <article className="step-card">
                <div className="step-icon-row">
                  <div className="step-icon blue">
                    <svg className="icon-lg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m8 6 4-4 4 4" /><path d="M12 2v10.3a4 4 0 0 1-1.172 2.872L4 22" /><path d="m20 22-5-5" /></svg>
                  </div>
                  <div className="step-number">1</div>
                </div>
                <h3>Enter your route and pick a class</h3>
                <p>Search airport transfers or hourly service with Sri Lanka locations already seeded for fast booking.</p>
              </article>
              <article className="step-card">
                <div className="step-icon-row">
                  <div className="step-icon blue">
                    <svg className="icon-lg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="8" y="2" width="8" height="4" rx="1" ry="1" /><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" /><path d="m9 14 2 2 4-4" /></svg>
                  </div>
                  <div className="step-number">2</div>
                </div>
                <h3>Complete the booking details</h3>
                <p>Add lead-passenger data, return timing, and extras without leaving the booking flow.</p>
              </article>
              <article className="step-card step-card-success">
                <div className="step-icon-row">
                  <div className="step-icon green">
                    <svg className="icon-lg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10" /><path d="m9 12 2 2 4-4" /></svg>
                  </div>
                  <div className="step-number success">3</div>
                </div>
                <h3>Meet your driver</h3>
                <p>Receive driver details ahead of pickup and enjoy a polished arrival with a visible name sign.</p>
              </article>
            </div>
          </div>
        </section>

        {/* VEHICLE FLEET */}
        <section className="fleet" aria-label="Vehicle fleet">
          <div className="container fleet-shell">
            <div className="fleet-header">
              <div>
                <span className="fleet-badge">Illustrative fleet</span>
                <h2>Premium transfer classes available across Sri Lanka.</h2>
                <p>A preview of the vehicle categories we offer. Exact pricing, availability, and vehicle assignment are confirmed during the booking process.</p>
              </div>
            </div>

            <div className="fleet-grid">
              <article className="vehicle-card">
                <div className="vehicle-img-wrap">
                  <img src={vehicleEconomy} alt="Economy sedan" />
                  <div className="vehicle-img-overlay"></div>
                  <span className="glass-pill vehicle-badge">Best value</span>
                </div>
                <div className="vehicle-copy">
                  <strong>Economy Sedan</strong>
                  <span>Ideal for quick airport exits and solo arrivals.</span>
                  <div className="vehicle-capacity">
                    <span className="capacity-item">
                      <svg className="icon-sm" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M22 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></svg>
                      3 passengers
                    </span>
                    <span className="capacity-item">
                      <svg className="icon-sm" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M6 20h0a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2h0" /><path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /><rect width="20" height="4" x="2" y="12" rx="0" /></svg>
                      3 luggage
                    </span>
                  </div>
                </div>
              </article>
              <article className="vehicle-card">
                <div className="vehicle-img-wrap">
                  <img src={vehicleStandardClass} alt="Standard sedan" />
                  <div className="vehicle-img-overlay"></div>
                  <span className="glass-pill vehicle-badge">Most popular</span>
                </div>
                <div className="vehicle-copy">
                  <strong>Standard Sedan</strong>
                  <span>A polished private sedan with meet-and-greet included.</span>
                  <div className="vehicle-capacity">
                    <span className="capacity-item"><svg className="icon-sm" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M22 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></svg> 3 passengers</span>
                    <span className="capacity-item"><svg className="icon-sm" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M6 20h0a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2h0" /><path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /><rect width="20" height="4" x="2" y="12" rx="0" /></svg> 3 luggage</span>
                  </div>
                </div>
              </article>
              <article className="vehicle-card">
                <div className="vehicle-img-wrap">
                  <img src={vehicleStandardVan} alt="Standard van" />
                  <div className="vehicle-img-overlay"></div>
                  <span className="glass-pill vehicle-badge">Family pick</span>
                </div>
                <div className="vehicle-copy">
                  <strong>Standard Van</strong>
                  <span>Room for families, surfboards, and long-haul baggage.</span>
                  <div className="vehicle-capacity">
                    <span className="capacity-item"><svg className="icon-sm" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M22 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></svg> 7 passengers</span>
                    <span className="capacity-item"><svg className="icon-sm" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M6 20h0a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2h0" /><path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /><rect width="20" height="4" x="2" y="12" rx="0" /></svg> 7 luggage</span>
                  </div>
                </div>
              </article>
              <article className="vehicle-card">
                <div className="vehicle-img-wrap">
                  <img src={vehicleMinibus} alt="Minibus" />
                  <div className="vehicle-img-overlay"></div>
                  <span className="glass-pill vehicle-badge">Group choice</span>
                </div>
                <div className="vehicle-copy">
                  <strong>Minibus</strong>
                  <span>Premium group transfers for weddings, tours, and teams.</span>
                  <div className="vehicle-capacity">
                    <span className="capacity-item"><svg className="icon-sm" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M22 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></svg> 12 passengers</span>
                    <span className="capacity-item"><svg className="icon-sm" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M6 20h0a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2h0" /><path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /><rect width="20" height="4" x="2" y="12" rx="0" /></svg> 12 luggage</span>
                  </div>
                </div>
              </article>
            </div>
          </div>
        </section>

        {/* FOOTER */}
        <footer className="footer">
          <div className="container footer-grid">
            <div className="footer-brand">
              <h2 style={{ color: '#fff' }}>LankaWayFinder</h2>
              <p>Your trusted airport transfer service in Sri Lanka, with polished arrivals from CMB to hotels, cities, and coastal stays.</p>
              <div className="footer-social">
                <a href="https://web.facebook.com/Lankawayfinder" target="_blank" rel="noopener noreferrer" aria-label="Facebook"><svg className="icon-md" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" /></svg></a>
                <a href="https://www.instagram.com/lankawayfinder/" target="_blank" rel="noopener noreferrer" aria-label="Instagram"><svg className="icon-md" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect width="20" height="20" x="2" y="2" rx="5" ry="5" /><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" /><line x1="17.5" x2="17.51" y1="6.5" y2="6.5" /></svg></a>
                <a href="mailto:rides@lankawayfinder.com" aria-label="Email"><svg className="icon-md" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect width="20" height="16" x="2" y="4" rx="2" /><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" /></svg></a>
              </div>
              <div className="footer-contact">
                <a href="tel:+94774052202" className="contact-link">
                  <svg className="icon-sm" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" /></svg>
                  +94 77 405 2202
                </a>
                <a href="mailto:rides@lankawayfinder.com" className="contact-link">
                  <svg className="icon-sm" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect width="20" height="16" x="2" y="4" rx="2" /><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" /></svg>
                  rides@lankawayfinder.com
                </a>
              </div>
            </div>
            <div>
              <h3>Company</h3>
              <a href="/about.html">About Us</a>
              <a href="#">Careers</a>
              <a href="#">Contact</a>
            </div>
            <div>
              <h3>Support</h3>
              <a href="/guides.html">Help Center</a>
              <a href="#">Safety</a>
              <a href="/faq.html">FAQ</a>
            </div>
            <div>
              <h3>Legal</h3>
              <a href="/privacy-policy.html">Privacy Policy</a>
              <a href="/terms.html">Terms &amp; Conditions</a>
              <a href="/refund-policy.html">Refund Policy</a>
              <a href="#">Cookie Policy</a>
            </div>
          </div>
          <div className="container footer-bottom">&copy; 2026 LankaWayFinder. All rights reserved.</div>
        </footer>
      </main>

      {/* WhatsApp Floating Button */}
      <button
        onClick={handleWhatsAppClick}
        className="fixed bottom-20 lg:bottom-6 right-6 z-50 bg-green-500 hover:bg-green-600 text-white p-4 rounded-full shadow-lg transition-all duration-300 hover:scale-110 flex items-center justify-center group"
        aria-label="Chat on WhatsApp"
        title="Chat with us on WhatsApp"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="currentColor"
          className="w-7 h-7"
        >
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
        </svg>
        <span className="absolute right-full mr-3 bg-gray-800 text-white text-sm px-3 py-2 rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none hidden lg:block">
          Chat with us!
        </span>
      </button>
    </div>
  );
};

export default HomePage;